import _ from 'lodash'

export const version = process.env.INDY_CLIENT_VERSION

export const gqlUri = process.env.GRAPHQL_HTTP_URI
export const serverUri = gqlUri.split('/graphql')[0]
export const actionCableUri = process.env.ACTION_CABLE_URI

export const stripeAPIToken = process.env.STRIPE_API_TOKEN
export const stripeStandardConnectApiToken = process.env.STRIPE_STANDARD_CONNECT_API_TOKEN

export const APP_MODE_TYPES = {
  REGISTER: 'pos',
  CONSUMER: 'consumer',
  KIOSK: 'kiosk',
  KDS: 'kds',
  ORDER_COLLECTION_DISPLAY: 'order-collection-display',
  ROAM: 'roam',
  SIGNAGE: 'signage',
  ADMIN: 'admin'
}
APP_MODE_TYPES.ALL = _.values(APP_MODE_TYPES)
APP_MODE_TYPES.MOVIEGOER = [APP_MODE_TYPES.CONSUMER, APP_MODE_TYPES.KIOSK, APP_MODE_TYPES.SIGNAGE]
Object.freeze(APP_MODE_TYPES)

export const APP_MODE_OPTIONS = APP_MODE_TYPES.ALL
export const MOVIEGOER_APP_MODES = APP_MODE_TYPES.MOVIEGOER

export const appMode = process.env.APP_MODE
export const isAdmin = appMode === APP_MODE_TYPES.ADMIN
export const isConsumer = appMode === APP_MODE_TYPES.CONSUMER
export const isKds = appMode === APP_MODE_TYPES.KDS
export const isOrderCollectionDisplay = appMode === APP_MODE_TYPES.ORDER_COLLECTION_DISPLAY
export const isKiosk = appMode === APP_MODE_TYPES.KIOSK
export const isMoviegoerAppMode = _.includes(MOVIEGOER_APP_MODES, appMode)
export const isPos = appMode === APP_MODE_TYPES.REGISTER

// Useful for services that need to be unit tested because a function easier to stub.
export const isPosFn = () => appMode === APP_MODE_TYPES.REGISTER

export const isRoam = appMode === APP_MODE_TYPES.ROAM
export const isSignage = appMode === APP_MODE_TYPES.SIGNAGE

export const hasActionCable = isAdmin || isPos || isKiosk || isKds || isRoam

export const APP_ENV_OPTIONS = Object.freeze(['development', 'production', 'integration', 'test'])
export const appEnv = process.env.APP_ENV
export const isElectronMode = window.electronPrinter !== undefined
export const isNative = window._capacitorNative !== undefined || window._cordovaNative !== undefined

export const siteId = isConsumer ? process.env.API_SITE_ID : null
export const circuitId = isConsumer ? process.env.API_CIRCUIT_ID : null

export const imgixHost = process.env.IMGIX_HOST || ''

// NOTE: When possible, prefer to use the configuration.currentSite field values instead of the following static values.
// These static values are intended for displaying content before the site configuration is loaded.

export const siteTitle = process.env.SITE_TITLE

export const indySplashImage = process.env.SPLASH_IMAGE || ''

export const lightLogoImage = process.env.LIGHT_LOGO_IMAGE
export const lightLogoImageUrl = `${imgixHost}/${process.env.LIGHT_LOGO_IMAGE}?w=400&png&auto=format,compress`

export const darkLogoImage = process.env.DARK_LOGO_IMAGE
export const darkLogoImageUrl = `${imgixHost}/${process.env.DARK_LOGO_IMAGE}?w=400&png&auto=format,compress`

export const lightLogoMarkImage = process.env.LIGHT_LOGO_MARK_IMAGE
export const lightLogoMarkImageUrl = `${imgixHost}/${process.env.LIGHT_LOGO_MARK_IMAGE}?w=400&h=400&png&auto=format,compress`

export const darkLogoMarkImage = process.env.DARK_LOGO_MARK_IMAGE
export const darkLogoMarkImageUrl = `${imgixHost}/${process.env.DARK_LOGO_MARK_IMAGE}?w=400&h=400&png&auto=format,compress`

export const REGION_ENDPOINTS = Object.freeze({
  US: 'admin.indy.systems',
  UK: 'admin-uk.indy.systems',
  NZ: 'admin-nz.indy.systems',
  AU: 'admin-au.indy.systems',
  'US-BETA': 'admin-us-beta.indy.systems',
  'US-EAST': 'admin-us-east.indy.systems',
  CB: 'admin-cb.indy.systems',
  GTC: 'admin-gtc.indy.systems',
  CINEPOLIS: 'admin-cinepolis.indy.systems'
})
export const US = 'us'
export const UK = 'uk'
export const NZ = 'nz'
export const AU = 'au'
export const US_BETA = 'us-beta'
export const US_EAST = 'us-east'
export const CB = 'cb'
export const CINEPOLIS = 'cinepolis'
export const GTC = 'gtc'
export const region = process.env.REGION

export const REGIONS = {
  AU,
  NZ,
  UK,
  US,
  US_BETA,
  US_EAST,
  CB,
  CINEPOLIS,
  GTC
}
export const isNARegion = [US, US_BETA, US_EAST, CB, CINEPOLIS, GTC].includes(region)
export const isUSBasedRegion = [US, US_BETA, US_EAST, GTC].includes(region)

// Dynamic region checkers for each region (isRegion.US, isRegion.UK, etc.)
export const isRegion = {}
for (const [key, value] of Object.entries(REGIONS)) {
  Object.defineProperty(isRegion, key, {
    get: () => region === value,
    enumerable: true
  })
}

export const googleRecaptchaKey = process.env.GOOGLE_RECAPTCHA_KEY || '6Lep_8wmAAAAAFzJ7Ce_AIdVUpjuJovh5MUZF9mV'

export const geoapifyEnabled = process.env.GEOAPIFY_API_KEY && process.env.GEOAPIFY_API_KEY.length
export const geoapifyApiKey = process.env.GEOAPIFY_API_KEY

export const rollbarEnv = process.env.ROLLBAR_ENV || appEnv

export const intercomAppId = process.env.INTERCOM_APP_ID || appEnv === 'production' ? 'mia9p1pi' : 'gpxuuc5k'

export const adminRoot =
  isAdmin
    ? '/'
    : '/admin/'
export const consumerRoot =
  (isConsumer || isKiosk)
    ? '/'
    : '/consumer/'
export const posRoot =
  isPos
    ? '/'
    : '/pos/'
export const signageRoot =
  isSignage
    ? '/'
    : '/signage/'
export const kioskRoot =
  isKiosk
    ? '/'
    : '/kiosk/'
export const kdsRoot =
    isKds
      ? '/'
      : '/kds/'
export const orderCollectionDisplayRoot =
  isOrderCollectionDisplay
    ? '/'
    : '/order-collection-display/'
export const roamRoot =
    isRoam
      ? '/'
      : '/roam/'

export default {
  gqlUri,
  serverUri,
  actionCableUri,
  stripeAPIToken,
  stripeStandardConnectApiToken,
  siteId,
  circuitId,
  appMode,
  siteTitle,
  region,
  adminRoot,
  consumerRoot,
  posRoot,
  signageRoot,
  kioskRoot,
  kdsRoot,
  orderCollectionDisplayRoot,
  roamRoot,
  lightLogoImageUrl,
  darkLogoImageUrl,
  lightLogoMarkImageUrl,
  darkLogoMarkImageUrl,
  version,
  googleRecaptchaKey,
  geoapifyEnabled,
  geoapifyApiKey
}
